/**
 * Vorlagen-System für Briefe, Anträge und Standardschreiben
 * - Vorlagen-Bibliothek
 * - KI-gestützte Platzhalter-Befüllung
 * - PDF-Export
 */

import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Download, Eye, Trash2, Edit,
  FileWarning, FilePlus, Mail, ClipboardList, Search,
  ChevronLeft, Printer, X, Check, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
// Dialog imports removed - all forms are now inline
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const KATEGORIE_ICONS = {
  widerspruch: FileWarning,
  antrag: FilePlus,
  mitteilung: Mail,
  brief: FileText,
  bericht: ClipboardList
};

export default function VorlagenPage() {
  const [vorlagen, setVorlagen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [klienten, setKlienten] = useState([]);
  const [selectedKategorie, setSelectedKategorie] = useState('alle');
  const [selectedVorlage, setSelectedVorlage] = useState(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [generierteDoc, setGenerierteDoc] = useState([]);

  // Briefkopf-Einstellungen (könnten aus Benutzer-Einstellungen kommen)
  const [briefkopf, setBriefkopf] = useState({
    name: 'Rechtliche Betreuung',
    strasse: 'Musterstraße 1',
    plz: '12345',
    ort: 'Musterstadt',
    telefon: '0123/456789',
    email: 'betreuer@beispiel.de'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vorl, kat, kl, gen] = await Promise.all([
        fetch(`${BACKEND_URL}/api/vorlagen/liste`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/vorlagen/kategorien/liste`).then(r => r.json()),
        betreuungAPI.listKlienten(),
        fetch(`${BACKEND_URL}/api/vorlagen/generierte/liste`).then(r => r.json())
      ]);
      setVorlagen(vorl);
      setKategorien(kat);
      setKlienten(kl);
      setGenerierteDoc(gen);
    } catch (e) {
      console.error(e);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredVorlagen = selectedKategorie === 'alle' 
    ? vorlagen 
    : vorlagen.filter(v => v.kategorie === selectedKategorie);

  const handleSelectVorlage = (vorlage) => {
    setSelectedVorlage(vorlage);
    setGeneratorOpen(true);
    setPreviewHtml('');
  };

  return (
    <div className="space-y-6" data-testid="vorlagen-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-6 h-6" /> Vorlagen & Schreiben
          </h1>
          <p className="text-slate-500 mt-1">Briefe, Widersprüche und Anträge erstellen</p>
        </div>
      </div>

      <Tabs defaultValue="vorlagen">
        <TabsList>
          <TabsTrigger value="vorlagen">Vorlagen-Bibliothek</TabsTrigger>
          <TabsTrigger value="generiert">Generierte Dokumente</TabsTrigger>
          <TabsTrigger value="briefkopf">Briefkopf-Einstellungen</TabsTrigger>
        </TabsList>

        {/* Vorlagen-Bibliothek */}
        <TabsContent value="vorlagen" className="space-y-4">
          {/* Kategorie-Filter */}
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant={selectedKategorie === 'alle' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedKategorie('alle')}
            >
              Alle
            </Button>
            {kategorien.map(k => {
              const Icon = KATEGORIE_ICONS[k.id] || FileText;
              return (
                <Button 
                  key={k.id}
                  variant={selectedKategorie === k.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedKategorie(k.id)}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {k.name}
                </Button>
              );
            })}
          </div>

          {/* Vorlagen-Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredVorlagen.map(v => {
              const Icon = KATEGORIE_ICONS[v.kategorie] || FileText;
              return (
                <Card 
                  key={v.id} 
                  className="cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => handleSelectVorlage(v)}
                  data-testid={`vorlage-${v.id}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{v.name}</p>
                        <p className="text-sm text-slate-500 line-clamp-2">{v.beschreibung}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {kategorien.find(k => k.id === v.kategorie)?.name || v.kategorie}
                          </Badge>
                          {v.ist_standard && (
                            <Badge className="text-xs bg-green-100 text-green-700">Standard</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredVorlagen.length === 0 && !loading && (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-14 h-14 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Keine Vorlagen in dieser Kategorie</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Generierte Dokumente */}
        <TabsContent value="generiert" className="space-y-4">
          {generierteDoc.length > 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-2 text-slate-500 font-medium">Dokument</th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Betreuter</th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Erstellt am</th>
                    <th className="px-4 py-2 text-slate-500 font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {generierteDoc.map(doc => (
                    <tr key={doc.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{doc.vorlage_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{doc.klient_name || '-'}</td>
                      <td className="px-4 py-3 text-slate-500">
                        {doc.created_date ? new Date(doc.created_date).toLocaleString('de-DE') : ''}
                      </td>
                      <td className="px-4 py-3">
                        <a 
                          href={`${BACKEND_URL}/api/vorlagen/pdf/${doc.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-14 h-14 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Dokumente generiert</p>
                <p className="text-sm text-slate-400 mt-1">Wählen Sie eine Vorlage um ein Dokument zu erstellen</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Briefkopf-Einstellungen */}
        <TabsContent value="briefkopf" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Briefkopf-Einstellungen</CardTitle>
              <CardDescription>Diese Daten erscheinen auf allen generierten Dokumenten</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name / Büro</Label>
                  <Input 
                    value={briefkopf.name} 
                    onChange={e => setBriefkopf(p => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Straße und Hausnummer</Label>
                  <Input 
                    value={briefkopf.strasse} 
                    onChange={e => setBriefkopf(p => ({ ...p, strasse: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>PLZ</Label>
                  <Input 
                    value={briefkopf.plz} 
                    onChange={e => setBriefkopf(p => ({ ...p, plz: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Ort</Label>
                  <Input 
                    value={briefkopf.ort} 
                    onChange={e => setBriefkopf(p => ({ ...p, ort: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input 
                    value={briefkopf.telefon} 
                    onChange={e => setBriefkopf(p => ({ ...p, telefon: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input 
                    value={briefkopf.email} 
                    onChange={e => setBriefkopf(p => ({ ...p, email: e.target.value }))}
                  />
                </div>
              </div>
              <Button onClick={() => toast.success('Briefkopf gespeichert')}>
                <Save className="w-4 h-4 mr-2" /> Speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Vorlage-Generator Dialog */}
      {selectedVorlage && (
        <VorlageGeneratorDialog
          open={generatorOpen}
          onClose={() => { setGeneratorOpen(false); setSelectedVorlage(null); setPreviewHtml(''); }}
          vorlage={selectedVorlage}
          klienten={klienten}
          briefkopf={briefkopf}
          previewHtml={previewHtml}
          setPreviewHtml={setPreviewHtml}
          onGenerated={() => { loadData(); }}
        />
      )}
    </div>
  );
}


function VorlageGeneratorDialog({ open, onClose, vorlage, klienten, briefkopf, previewHtml, setPreviewHtml, onGenerated }) {
  const [formData, setFormData] = useState({});
  const [selectedKlientId, setSelectedKlientId] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && vorlage) {
      // Initiale Formular-Daten mit Defaults
      const initial = {};
      vorlage.felder?.forEach(f => {
        if (f.default) initial[f.name] = f.default;
      });
      setFormData(initial);
      setSelectedKlientId('');
      setPreviewHtml('');
    }
  }, [open, vorlage, setPreviewHtml]);

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePreview = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/vorlagen/generieren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vorlage_id: vorlage.id,
          klient_id: selectedKlientId || null,
          daten: formData,
          briefkopf
        })
      });
      const data = await res.json();
      if (data.html) {
        setPreviewHtml(data.html);
      } else {
        toast.error(data.detail || 'Fehler bei der Vorschau');
      }
    } catch (e) {
      toast.error('Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/vorlagen/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vorlage_id: vorlage.id,
          klient_id: selectedKlientId || null,
          daten: formData,
          briefkopf
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        toast.error(error.detail || 'PDF-Generierung fehlgeschlagen');
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${vorlage.id}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('PDF wurde heruntergeladen');
      onGenerated();
    } catch (e) {
      console.error(e);
      toast.error('Fehler beim PDF-Download');
    } finally {
      setGenerating(false);
    }
  };

  const renderField = (field) => {
    switch (field.typ) {
      case 'textarea':
        return (
          <Textarea 
            value={formData[field.name] || ''} 
            onChange={e => handleFieldChange(field.name, e.target.value)}
            rows={4}
            placeholder={field.placeholder}
          />
        );
      case 'select':
        return (
          <Select 
            value={formData[field.name] || ''} 
            onValueChange={v => handleFieldChange(field.name, v)}
          >
            <SelectTrigger><SelectValue placeholder="Auswählen..." /></SelectTrigger>
            <SelectContent>
              {field.optionen?.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'date':
        return (
          <Input 
            type="date" 
            value={formData[field.name] || ''} 
            onChange={e => handleFieldChange(field.name, e.target.value)}
          />
        );
      case 'number':
        return (
          <Input 
            type="number" 
            value={formData[field.name] || ''} 
            onChange={e => handleFieldChange(field.name, e.target.value)}
          />
        );
      default:
        return (
          <Input 
            value={formData[field.name] || ''} 
            onChange={e => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  return (
    <Card className="max-w-5xl mx-auto mt-4 shadow-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="vorlagen-editor">
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {vorlage?.name}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <CardDescription>{vorlage?.beschreibung}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">

        <div className="flex-1 overflow-hidden flex gap-4">
          {/* Linke Seite: Formular */}
          <div className="w-1/2 overflow-y-auto pr-4 space-y-4">
            {/* Betreuter auswählen */}
            <div>
              <Label>Betreuter</Label>
              <Select value={selectedKlientId || 'none'} onValueChange={v => setSelectedKlientId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Betreuten auswählen (optional)..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Kein Betreuter --</SelectItem>
                  {klienten.map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamische Felder */}
            {vorlage?.felder?.map(field => (
              <div key={field.name}>
                <Label>
                  {field.label}
                  {field.pflicht && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}

            <div className="flex gap-2 pt-4">
              <Button onClick={handlePreview} disabled={generating} variant="outline">
                <Eye className="w-4 h-4 mr-2" />
                Vorschau
              </Button>
              <Button onClick={handleDownloadPdf} disabled={generating}>
                <Download className="w-4 h-4 mr-2" />
                PDF erstellen
              </Button>
            </div>
          </div>

          {/* Rechte Seite: Vorschau */}
          <div className="w-1/2 border border-slate-200 rounded-lg overflow-hidden bg-white">
            {previewHtml ? (
              <iframe 
                srcDoc={previewHtml}
                className="w-full h-full"
                title="Vorschau"
                style={{ minHeight: '500px' }}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Klicken Sie auf "Vorschau" um das Dokument zu sehen</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t shrink-0">
          <Button variant="outline" onClick={onClose}>Schliessen</Button>
        </div>
      </CardContent>
    </Card>
  );
}
